import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import './FinancialReports.css';

const FinancialReports = ({ embedded = false }) => {
  const { user } = useAuth();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState('summary');
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  
  const [financialSummary, setFinancialSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [revenueCategories, setRevenueCategories] = useState([]);

  useEffect(() => {
    fetchExpenseCategories();
    fetchRevenueCategories();
  }, []);

  useEffect(() => {
    if (activeTab === 'summary') {
      fetchFinancialSummary();
    } else if (activeTab === 'expenses') {
      fetchExpenses();
    }
  }, [activeTab, dateRange]);

  const fetchFinancialSummary = async () => {
    try {
      const token = user?.token;
      const params = new URLSearchParams(dateRange);
      const response = await fetch(`${API_BASE_URL}/api/financial/summary?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFinancialSummary(data);
      }
    } catch (error) {
      console.error('Error fetching financial summary:', error);
    }
  };

  const fetchExpenses = async () => {
    try {
      const token = user?.token;
      const params = new URLSearchParams(dateRange);
      const response = await fetch(`${API_BASE_URL}/api/expenses?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setExpenses(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      const token = user?.token;
      const response = await fetch(`${API_BASE_URL}/api/expenses/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setExpenseCategories(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching expense categories:', error);
    }
  };

  const fetchRevenueCategories = async () => {
    try {
      const token = user?.token;
      const response = await fetch(`${API_BASE_URL}/api/revenue/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRevenueCategories(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching revenue categories:', error);
    }
  };

  const handlePLReport = async () => {
    try {
      const token = user?.token;
      const params = new URLSearchParams(dateRange);
      const response = await fetch(`${API_BASE_URL}/api/financial/summary?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) { toast.error('No financial data for selected period'); return; }
      const data = await response.json();
      const rows = [
        ['P&L Statement', `${dateRange.start_date} to ${dateRange.end_date}`],
        [''],
        ['Revenue', formatCurrency(data.revenue?.total_revenue)],
        ['Expenses', formatCurrency(data.expenses?.total_expenses)],
        ['Net Income', formatCurrency(data.net_income)],
        [''],
        ['Expense Category', 'Amount'],
        ...(data.expensesByCategory || []).map(c => [c.category_name, formatCurrency(c.category_total)])
      ];
      const csv = rows.map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pl-statement-${dateRange.start_date}-${dateRange.end_date}.csv`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (error) {
      console.error('Error generating P&L report:', error);
      toast.error('Error generating P&L report');
    }
  };

  const handleRevenueReport = async () => {
    try {
      const token = user?.token;
      const params = new URLSearchParams(dateRange);
      const response = await fetch(`${API_BASE_URL}/api/financial/summary?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) { toast.error('No revenue data for selected period'); return; }
      const data = await response.json();
      const rows = [
        ['Revenue by Source', `${dateRange.start_date} to ${dateRange.end_date}`],
        [''],
        ['Total Revenue', formatCurrency(data.revenue?.total_revenue)],
        ['Active Subscriptions', data.revenue?.active_subscriptions || 0],
        [''],
        ['Category', 'Total'],
        ...(data.revenueByCategory || data.expensesByCategory || []).map(c => [
          c.category_name, formatCurrency(c.category_total || c.total || 0)
        ])
      ];
      const csv = rows.map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `revenue-by-source-${dateRange.start_date}-${dateRange.end_date}.csv`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (error) {
      console.error('Error generating revenue report:', error);
      toast.error('Error generating revenue report');
    }
  };

  const handleQuickBooksExport = async () => {
    try {
      const token = user?.token;
      const params = new URLSearchParams(dateRange);
      const response = await fetch(`${API_BASE_URL}/api/financial/quickbooks-export?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `quickbooks-export-${dateRange.start_date}-${dateRange.end_date}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('QuickBooks export downloaded successfully!');
      } else {
        toast.warning('No data to export for selected date range');
      }
    } catch (error) {
      console.error('Error exporting to QuickBooks:', error);
      toast.error('Error generating export');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div className="financial-reports-container">
      {!embedded && (
        <div className="financial-header">
          <h1>💰 Financial Reports & Accounting</h1>
          <p>Comprehensive financial management and reporting</p>
        </div>
      )}

      {/* Date Range Selector */}
      <div className="date-range-selector">
        <div className="date-inputs">
          <div>
            <label>Start Date:</label>
            <input
              type="date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({...dateRange, start_date: e.target.value})}
            />
          </div>
          <div>
            <label>End Date:</label>
            <input
              type="date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({...dateRange, end_date: e.target.value})}
            />
          </div>
        </div>
        <button className="btn-export" onClick={handleQuickBooksExport}>
          📊 Export to QuickBooks
        </button>
      </div>

      {/* Tabs */}
      <div className="financial-tabs">
        <button 
          className={activeTab === 'summary' ? 'tab-active' : ''}
          onClick={() => setActiveTab('summary')}
        >
          Summary
        </button>
        <button 
          className={activeTab === 'expenses' ? 'tab-active' : ''}
          onClick={() => setActiveTab('expenses')}
        >
          Expenses
        </button>
        <button 
          className={activeTab === 'revenue' ? 'tab-active' : ''}
          onClick={() => setActiveTab('revenue')}
        >
          Revenue
        </button>
        <button 
          className={activeTab === 'reports' ? 'tab-active' : ''}
          onClick={() => setActiveTab('reports')}
        >
          Tax Reports
        </button>
      </div>

      {/* Summary Tab */}
      {activeTab === 'summary' && financialSummary && (
        <div className="tab-content">
          <h2>Financial Summary</h2>
          
          <div className="summary-cards">
            <div className="summary-card revenue-card">
              <div className="card-icon">💵</div>
              <div className="card-content">
                <h3>Total Revenue</h3>
                <p className="amount">{formatCurrency(financialSummary.revenue?.total_revenue)}</p>
                <span className="subtitle">{financialSummary.revenue?.active_subscriptions} active subscriptions</span>
              </div>
            </div>

            <div className="summary-card expense-card">
              <div className="card-icon">💸</div>
              <div className="card-content">
                <h3>Total Expenses</h3>
                <p className="amount">{formatCurrency(financialSummary.expenses?.total_expenses)}</p>
                <span className="subtitle">{financialSummary.expenses?.expense_count} transactions</span>
              </div>
            </div>

            <div className={`summary-card profit-card ${financialSummary.net_income >= 0 ? 'positive' : 'negative'}`}>
              <div className="card-icon">{financialSummary.net_income >= 0 ? '✅' : '⚠️'}</div>
              <div className="card-content">
                <h3>Net Income</h3>
                <p className="amount">{formatCurrency(financialSummary.net_income)}</p>
                <span className="subtitle">{financialSummary.net_income >= 0 ? 'Profitable' : 'Operating at loss'}</span>
              </div>
            </div>
          </div>

          <div className="expenses-by-category">
            <h3>Expenses by Category</h3>
            <div className="category-breakdown">
              {financialSummary.expensesByCategory && financialSummary.expensesByCategory.length > 0 ? (
                financialSummary.expensesByCategory.map((cat, idx) => (
                  <div key={idx} className="category-row">
                    <span className="category-name">{cat.category_name}</span>
                    <div className="category-bar">
                      <div 
                        className="category-fill"
                        style={{
                          width: `${(cat.category_total / financialSummary.expenses.total_expenses) * 100}%`
                        }}
                      ></div>
                    </div>
                    <span className="category-amount">{formatCurrency(cat.category_total)}</span>
                  </div>
                ))
              ) : <p>No expense data available</p>}
            </div>
          </div>
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div className="tab-content">
          <h2>Expense Transactions</h2>
          {expenses.length > 0 ? (
            <div className="expenses-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Vendor</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Payment Method</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map(expense => (
                    <tr key={expense.id}>
                      <td>{expense.expense_date}</td>
                      <td>{expense.category_name}</td>
                      <td>{expense.vendor_name || 'N/A'}</td>
                      <td>{expense.description}</td>
                      <td className="amount-cell">{formatCurrency(expense.amount)}</td>
                      <td>{expense.payment_method}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No expenses recorded for this period</p>
            </div>
          )}
        </div>
      )}

      {/* Revenue Tab */}
      {activeTab === 'revenue' && (
        <div className="tab-content">
          <h2>Revenue Streams</h2>
          <div className="revenue-categories-grid">
            {revenueCategories.map(cat => (
              <div key={cat.id} className="revenue-category-card">
                <h3>{cat.category_name}</h3>
                <p>{cat.description}</p>
                <span className={`badge ${cat.is_taxable ? 'taxable' : 'non-taxable'}`}>
                  {cat.is_taxable ? 'Taxable' : 'Non-Taxable'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tax Reports Tab */}
      {activeTab === 'reports' && (
        <div className="tab-content">
          <h2>Tax & Accounting Reports</h2>
          <div className="reports-grid">
            <div className="report-card">
              <h3>📋 Year-End Summary</h3>
              <p>Comprehensive annual financial summary for tax preparation</p>
              <button className="btn-primary" onClick={handleQuickBooksExport}>Generate Report</button>
            </div>
            <div className="report-card">
              <h3>📊 P&L Statement</h3>
              <p>Profit and loss statement for selected period</p>
              <button className="btn-primary" onClick={handlePLReport}>Generate Report</button>
            </div>
            <div className="report-card">
              <h3>🧾 Expense Summary</h3>
              <p>Detailed expense breakdown by category</p>
              <button className="btn-primary" onClick={handleQuickBooksExport}>Generate Report</button>
            </div>
            <div className="report-card">
              <h3>💳 Revenue by Source</h3>
              <p>Revenue analysis by category</p>
              <button className="btn-primary" onClick={handleRevenueReport}>Generate Report</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialReports;
